import { baseApi } from "./baseApi";

const AUTHORIZATION_BASE_URL = "/authorizations";

export const authorizationApi = baseApi.injectEndpoints({
  tagTypes: ["Authorization"],

  endpoints: (build) => ({
    listAuthorization: build.query({
      query: (status) => `${AUTHORIZATION_BASE_URL}/list?status=${status}`,
      // Provides a list of `Authorizations` by `id`.
      // If any mutation is executed that `invalidate`s any of these tags, this query will re-run to be always up-to-date.
      // The `LIST` id is a "virtual id" we just made up to be able to invalidate this query specifically if a new `Authorizations` element was added.
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: "Authorization", id })),
              { type: "Authorization", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: '', id: 'LIST' }` is invalidated
            [{ type: "Authorization", id: "LIST" }],
    }),

    addAuthorization: build.mutation({
      query(body) {
        return {
          url: `${AUTHORIZATION_BASE_URL}/add`,
          method: "POST",
          body,
        };
      },
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created authorization could show up in any lists.
      invalidatesTags: [{ type: "Authorization", id: "LIST" }],
    }),

    detailAuthorization: build.query({
      query: (id) => `${AUTHORIZATION_BASE_URL}/detail/${id}`,

      // Pick out data and prevent nested properties in a hook or selector (from documentation but not used*)
      // transformResponse: (response) => response.data,

      providesTags: (result, error, id) => [{ type: "Authorization", id }],
    }),

    editAuthorization: build.mutation({
      query(data) {
        const { id, ...body } = data; // id is separated because we don't want to create a new object, just an update
        return {
          url: `${AUTHORIZATION_BASE_URL}/edit/${id}`,
          method: "PUT",
          body, //body without the _id property
        };
      },
      // Invalidates all queries that subscribe to this Authorization `id` only.
      // In this case, `detailAuthorization` will be re-run. `listAuthorization` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [
        { type: "Authorization", id },
        { type: "Authorization", id: "LIST" },
      ],
    }),

    deleteAuthorization: build.mutation({
      query(id) {
        return {
          url: `${AUTHORIZATION_BASE_URL}/delete/${id}`,
          method: "DELETE",
        };
      },
      // Invalidates all queries that subscribe to this Authorization `id` only.
      invalidatesTags: (result, error, id) => [
        { type: "Authorization", id },
        { type: "Authorization", id: "LIST" },
      ],
    }),

    // Specific Slice for Entity-level

    addEntityAuthorization: build.mutation({
      query(body) {
        return {
          url: `${AUTHORIZATION_BASE_URL}/add/entity`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Authorization", id: "LIST" }],
    }),

    listEntityAuthorization: build.query({
      query: (status) =>
        `${AUTHORIZATION_BASE_URL}/list/entity?status=${status}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Authorization", id })),
              { type: "Authorization", id: "LIST" },
            ]
          : [{ type: "Authorization", id: "LIST" }],
    }),

    editEntityAuthorization: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `${AUTHORIZATION_BASE_URL}/edit/${id}/entity`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Authorization", id },
        { type: "Authorization", id: "LIST" },
      ],
    }),

    // Specific Slice for Account-level

    listAccountAuthorization: build.query({
      query: (status) =>
        `${AUTHORIZATION_BASE_URL}/list/account?status=${status}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Authorization", id })),
              { type: "Authorization", id: "LIST" },
            ]
          : [{ type: "Authorization", id: "LIST" }],
    }),
    listSystemAuthorization: build.query({
      query: (status) =>
        `${AUTHORIZATION_BASE_URL}/list/system?status=${status}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Authorization", id })),
              { type: "Authorization", id: "LIST" },
            ]
          : [{ type: "Authorization", id: "LIST" }],
    }),

    /*************/
    getDDLAuthorizationList: build.query({
      query: (params) => ({ url: `${AUTHORIZATION_BASE_URL}/ddl`, params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Authorization", id })),
              { type: "Authorization", id: "DDL" },
            ]
          : [{ type: "Authorization", id: "DDL" }],
    }),
  }),

  overrideExisting: false,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the name of the endpoint in the service definition [eg: useEndpointQuery or useEndpointMutation ]
export const {
  useAddAuthorizationMutation,
  useDetailAuthorizationQuery,
  useEditAuthorizationMutation,
  useDeleteAuthorizationMutation,
  useAddEntityAuthorizationMutation,
  useListEntityAuthorizationQuery,
  useEditEntityAuthorizationMutation,
  useListAccountAuthorizationQuery,
  useListSystemAuthorizationQuery,

  useGetDDLAuthorizationListQuery,
  useLazyGetDDLAuthorizationListQuery,
} = authorizationApi;
